/**
 * Copyright 2019, 2024, 2025, Optimizely
 *
 * Licensed under the Apache License, Version 2.0 (the License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 * http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { OptimizelyError } from '../error/optimizly_error';
import { sprintf } from '../utils/fns';
export var LogLevel;
(function (LogLevel) {
    LogLevel[LogLevel["Debug"] = 0] = "Debug";
    LogLevel[LogLevel["Info"] = 1] = "Info";
    LogLevel[LogLevel["Warn"] = 2] = "Warn";
    LogLevel[LogLevel["Error"] = 3] = "Error";
})(LogLevel || (LogLevel = {}));
export const LogLevelToUpper = {
    [LogLevel.Debug]: 'DEBUG',
    [LogLevel.Info]: 'INFO',
    [LogLevel.Warn]: 'WARN',
    [LogLevel.Error]: 'ERROR',
};
export const LogLevelToLower = {
    [LogLevel.Debug]: 'debug',
    [LogLevel.Info]: 'info',
    [LogLevel.Warn]: 'warn',
    [LogLevel.Error]: 'error',
};
export class ConsoleLogHandler {
    constructor(prefix) {
        this.prefix = prefix || '[OPTIMIZELY]';
    }
    log(level, message) {
        const log = `${this.prefix} - ${LogLevelToUpper[level]} ${this.getTime()} ${message}`;
        this.consoleLog(level, log);
    }
    getTime() {
        return new Date().toISOString();
    }
    consoleLog(logLevel, log) {
        const methodName = LogLevelToLower[logLevel];
        const method = console[methodName] || console.log;
        method.call(console, log);
    }
}
export class OptimizelyLogger {
    constructor(config) {
        this.prefix = '';
        this.logHandler = config.logHandler;
        this.infoResolver = config.infoMsgResolver;
        this.errorResolver = config.errorMsgResolver;
        this.level = config.level;
        if (config.name) {
            this.setName(config.name);
        }
    }
    child(name) {
        return new OptimizelyLogger({
            logHandler: this.logHandler,
            infoMsgResolver: this.infoResolver,
            errorMsgResolver: this.errorResolver,
            level: this.level,
            name,
        });
    }
    setName(name) {
        this.name = name;
        this.prefix = `${name}: `;
    }
    info(message, ...args) {
        this.log(LogLevel.Info, message, args);
    }
    debug(message, ...args) {
        this.log(LogLevel.Debug, message, args);
    }
    warn(message, ...args) {
        this.log(LogLevel.Warn, message, args);
    }
    error(message, ...args) {
        this.log(LogLevel.Error, message, args);
    }
    handleLog(level, message, args) {
        const log = args.length > 0 ? `${this.prefix}${sprintf(message, ...args)}`
            : `${this.prefix}${message}`;
        this.logHandler.log(level, log);
    }
    log(level, message, args) {
        if (level < this.level) {
            return;
        }
        if (message instanceof Error) {
            if (message instanceof OptimizelyError) {
                message.setMessage(this.errorResolver);
            }
            this.handleLog(level, message.message, []);
            return;
        }
        let resolver = this.errorResolver;
        if (level < LogLevel.Warn) {
            if (!this.infoResolver) {
                return;
            }
            resolver = this.infoResolver;
        }
        const resolvedMessage = resolver.resolve(message);
        this.handleLog(level, resolvedMessage, args);
    }
}
export const __platforms = ['__universal__'];
//# sourceMappingURL=logger.js.map